import React, { useContext, useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import ErrorPage from '@mymoons/error-page';
import { PageLoader } from '@mymoons/ui-library';
import Header from '../../components/Header';
import Dashboard from '../../containers/Dashboard';
import { axiosGet, axiosGetOrderId } from '../../methods/globalData/axiosGet';
import ProductContext from '../../context/Product/Context';
import GlobalContext from '../../context/Global/Context';
import Constants from '../../utils/Constants';
import AlertModal from '../../components/AlertModal';
import { priceCheck, timer, backCheckout } from '../../assets/images/Global';
import { axiosPutRecalculateCarts } from '../../methods/products/axiosPut';
import { areThereCartsWithoutEnoughStock } from '../../utils/stock';
import { axiosPutSetShippingMethod } from '../../methods/globalData/axiosPut';
import alertModalInfo from './alertModalInfo';
import getDataBack4app from '../../utils/back4app';
import dateFormatter from '../../utils/dateFormatter';

class CustomErrorMessage extends Error {
  constructor(message, description) {
    super(message, description);
    this.name = `${description}`;
  }
}

const Home = () => {
  const carts = new URLSearchParams(window.location.search).getAll('cart');
  const name = new URLSearchParams(window.location.search).get('name');
  const redirectUrl = new URLSearchParams(window.location.search).get(
    'redirectUrl',
  );
  const publicKey = new URLSearchParams(window.location.search).get(
    'redirectUrl',
  );
  const origin = new URLSearchParams(window.location.search).get(
    'origin',
  );
  const buttonUrl = new URLSearchParams(window.location.search).get(
    'buttonUrl',
  );
  const customerId = new URLSearchParams(window.location.search).get(
    'customerId',
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [getCartsData, setGetCartsData] = useState([]);
  const [lineItems, setLineItems] = useState([]);
  const [cartPosition, setCartPosition] = useState(0);
  const [couponPosition, setCouponPosition] = useState(0);
  const [couponsReady, setCouponsReady] = useState(false);
  const [hasCoupon, setHasCoupon] = useState(false);
  const [cartOrdered, setCartOrdered] = useState(false);
  const [addressToModal, setAddressToModal] = useState([]);
  const [orderedType, setOrderedType] = useState('');
  const productContext = useContext(ProductContext);
  const globalContext = useContext(GlobalContext);
  const {
    setOriginFunction,
    setRedirectUrlFunction,
    setPublicKeyFunction,
    setIsCitaFlowFunction,
    setButtonUrlFunction,
    setCustomerIdFunction,
    setCartCountryFunction,
    setAppointmentDateFunction,
    setAppointmentCenterFunction,
    isCitaFlow,
    country,
  } = globalContext;
  const {
    setCarts,
    setProducts,
    setCoupons,
    coupons,
    couponsCode,
    setCouponsCode,
  } = productContext;
  const { CitaId, ShippingId } = Constants;

  /**
   * This function returns all Modal Data
   */
  const modalStrategies = alertModalInfo(
    isCitaFlow,
    priceCheck,
    buttonUrl,
    publicKey,
    redirectUrl,
    timer,
    addressToModal,
    backCheckout,
    setCartOrdered,
  );

  useEffect(() => {
    (async () => {
      try {
        const { data: upToDateCartData } = await axiosPutRecalculateCarts(carts);

        if (!areThereCartsWithoutEnoughStock(upToDateCartData.carts)) return;

        setOrderedType('outOfStock');
        setCartOrdered(true);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        setError(true);
      }
    })();
  }, []);

  useEffect(() => {
    if (isCitaFlow) {
      setTimeout(() => {
        setOrderedType('timeOut');
        setCartOrdered(true);
      }, 600000);
    }
  }, [isCitaFlow]);

  useEffect(() => {
    if (cartPosition <= carts.length) {
      const urlGenerated = `carts/${carts[cartPosition]}`;
      axiosGet(urlGenerated, true)
        .then((res) => {
          const { data } = res;
          if (country === '') {
            setCartCountryFunction(data.country.toLocaleUpperCase());
          }
          setGetCartsData([...getCartsData, data]);
          setCoupons(data.discountCodes);
          if (data.discountCodes.length > 0) {
            setHasCoupon(true);
          }
          setLineItems([...lineItems, data.lineItems]);

          if (cartPosition + 1 < carts.length) {
            setCartPosition(cartPosition + 1);
          }
        })
        .catch((errorSentry) => {
          const errorToSentry = new CustomErrorMessage(errorSentry, 'Error al obtener el carrito');
          if (Sentry) {
            Sentry.captureException(errorToSentry);
          }
          setError(true);
        });
    }
    if (name) {
      localStorage.setItem('name', name);
    } else {
      localStorage.setItem('name', 'Usuario Invitado');
    }
    if (redirectUrl) {
      setRedirectUrlFunction(redirectUrl);
    }
    if (publicKey) {
      setPublicKeyFunction(publicKey.split('/')[3]);
      // Get Appointment Information
      getDataBack4app('Patient', publicKey.split('/')[3], null)
        .then((responeDealAppointment) => {
          const appointmentDate = dateFormatter(
            responeDealAppointment.data.Patient.Appointment_Date.iso,
          );
          setAppointmentDateFunction(appointmentDate);
          setAppointmentCenterFunction(responeDealAppointment.data.Patient.Calendar);
        })
        .catch(() => {
          // eslint-disable-next-line
          console.log('No se pudo obtener la información de la cita');
        });
    }
    if (origin) {
      setOriginFunction(origin);
    }
    if (buttonUrl) {
      setButtonUrlFunction(buttonUrl);
    }
    if (customerId) {
      setCustomerIdFunction(customerId);
    }
  }, [cartPosition, name, redirectUrl, publicKey, origin, buttonUrl, customerId]);

  useEffect(() => {
    if (hasCoupon) {
      if (couponPosition <= coupons.length) {
        const urlGenerated = `/discount-code/${coupons[couponPosition].discountCode.id}`;
        axiosGet(urlGenerated, true)
          .then((res) => {
            const { data } = res;
            setCouponsCode([...couponsCode, { id: data.id, code: data.code }]);
            if (couponPosition + 1 < coupons.length) {
              setCouponPosition(cartPosition + 1);
            } else {
              setCouponsReady(true);
            }
          })
          .catch((errors) => {
            // eslint-disable-next-line
            console.log(errors);
          });
      } else {
        setCouponsReady(true);
      }
    } else {
      setCouponsReady(true);
    }
  }, [coupons, couponPosition, hasCoupon]);

  useEffect(() => {
    if (carts.length === getCartsData.length && couponsReady) {
      setCarts(getCartsData);
      setProducts(lineItems);
      setHasCoupon(false);
      if (lineItems.length !== 0) {
        // const price = totalPriceCarts(getCartsData);
        if (lineItems[0][0].productId === CitaId) {
          setIsCitaFlowFunction(true);
        } else {
          setIsCitaFlowFunction(false);
        }
      }
      setLoading(false);
    }
  }, [getCartsData, lineItems, couponsReady]);

  if (addressToModal.length === 0 && getCartsData.length > 0) {
    setAddressToModal(getCartsData[0]);
  }

  useEffect(() => {
    if (getCartsData[0]) {
      axiosGetOrderId(getCartsData[0].id)
        .then((response) => {
          if (response.data.results.length !== 0) {
            setCartOrdered(true);
            if (response.data.results[0].paymentState === 'Paid') {
              setOrderedType('paid');
            } else {
              setOrderedType('pending');
            }
          }
        })
        .catch((errorOrderId) => {
          // eslint-disable-next-line
          console.log(errorOrderId);
        });
      if (!getCartsData[0].shippingInfo) {
        const shippingMethod = {
          id: ShippingId,
          typeId: 'shipping-method',
        };
        axiosPutSetShippingMethod(carts, shippingMethod);
      }
    }
  }, [getCartsData[0]]);

  if (loading && !error) {
    return <PageLoader />;
  }

  if (error || carts.length === 0) {
    return <ErrorPage />;
  }

  return (
    <>
      <Header />
      <Dashboard />
      {cartOrdered && orderedType !== '' && (
        <AlertModal
          data={modalStrategies[orderedType].data}
          modal={modalStrategies[orderedType].modal && setCartOrdered}
          discardOption={modalStrategies[orderedType].discardOption}
        />
      )}
    </>
  );
};

export default Home;
